// Ширина колонок таблицы дизайн-блока по содержимому — правило дизайнера, а не
// HTML-авто (`table-layout: auto` давал колонкам с похожим текстом разную ширину).
// Порт мобилки (`design-block/table-columns.ts`) 1:1:
//   1. у колонки две ширины (с боковыми полями ячейки): «строка» — самая длинная
//      ячейка в одну строку, «минимум» — самое длинное слово (его не переносим);
//   2. свободное место делим поровну между нерешёнными колонками; кому доли
//      хватает на строку — фиксируем по строке, освободившееся достаётся
//      остальным; если таких нет — кому доля меньше минимума, фиксируем по
//      минимуму; повторяем, пока кого-то фиксируем;
//   3. всем хватило строки — лишнее поровну, таблица во всю ширину; не влезают
//      даже минимумы — сжимаем пропорционально минимумам (у дизайн-блока нет
//      прокрутки ни вверх-вниз, ни вбок — как в мобилке).
// Чистая функция: замеры делает `useTableColumns` в DOM.

/** Замеры текста: ширина ячейки в одну строку и ширина каждого её слова. */
export type TextWidths = Map<string, number>

export const cellKey = (row: number, col: number): string => `c:${row}:${col}`
export const wordKey = (row: number, col: number, word: string): string => `w:${row}:${col}:${word}`

/** Слова ячейки — по пробелам, без пустых. */
export const cellWords = (text: string): string[] => text.split(/\s+/).filter(Boolean)

interface ColumnWidthsArgs {
  texts: string[][]
  widths: TextWidths
  /** Боковые поля ячейки в сумме (0, если замер уже их включает). */
  cellPadding: number
  /** Ширина таблицы, если всё влезает. */
  available: number
}

const sum = (values: number[]): number => values.reduce((total, value) => total + value, 0)

/** Строка и минимум каждой колонки. +1 — запас на округление: слово впритык
 *  иначе может уехать на перенос. */
const columnBounds = ({ texts, widths, cellPadding }: ColumnWidthsArgs, columns: number) => {
  const min: number[] = []
  const max: number[] = []
  for (let col = 0; col < columns; col++) {
    let word = 0
    let line = 0
    texts.forEach((row, r) => {
      const text = row[col] ?? ''
      for (const w of cellWords(text)) word = Math.max(word, widths.get(wordKey(r, col, w)) ?? 0)
      line = Math.max(line, widths.get(cellKey(r, col)) ?? 0)
    })
    min.push(Math.ceil(word) + 1 + cellPadding)
    max.push(Math.ceil(line) + 1 + cellPadding)
  }
  return { min, max }
}

/** Раздача «водой»: каждая фиксация меняет долю остальных. */
const distribute = (min: number[], max: number[], available: number): number[] => {
  const widths: (number | null)[] = min.map(() => null)
  for (;;) {
    const open = widths.flatMap((width, col) => (width === null ? [col] : []))
    if (open.length === 0) break
    const share = (available - sum(widths.map((width) => width ?? 0))) / open.length
    // Сначала тех, кому хватает строки: они освобождают место остальным.
    const fits = open.filter((col) => max[col] <= share)
    const tooNarrow = fits.length ? [] : open.filter((col) => min[col] >= share)
    fits.forEach((col) => (widths[col] = max[col]))
    tooNarrow.forEach((col) => (widths[col] = min[col]))
    if (fits.length + tooNarrow.length === 0) {
      open.forEach((col) => (widths[col] = share))
      break
    }
  }
  return widths.map((width) => width ?? 0)
}

/** Ширины колонок; в сумме всегда `available`. */
export const tableColumnWidths = (args: ColumnWidthsArgs): number[] => {
  const { texts, available } = args
  const columns = texts.length === 0 ? 0 : Math.max(...texts.map((row) => row.length))
  if (columns === 0) return []
  const { min, max } = columnBounds(args, columns)
  const minTotal = sum(min)
  if (minTotal >= available) return min.map((width) => (width / minTotal) * available)
  const result = distribute(min, max, available)
  const extra = available - sum(result)
  return extra > 0 ? result.map((width) => width + extra / columns) : result
}
